/**
 * Firestore-backed rooms API for data, messages, and subscriptions.
 *
 * Handles direct Firestore operations for room data (like mobile app).
 * Room lifecycle (create, join, leave, start, kick) is handled by HttpRoomsApi.
 *
 * Paths:
 * - Room document: h5_rooms/{roomId}
 * - Messages: h5_rooms/{roomId}/messages
 * - Proposed moves: h5_rooms/{roomId}/proposed_moves
 *
 * ⚠️ PLAYGROUND-ONLY: This file depends on Firebase and should ONLY be imported by PlaygroundHost.
 * Do NOT import from barrel exports (rooms/index.ts) - use direct import path instead.
 * This ensures Firebase is not bundled in production builds.
 */
import {
  RoomSubscriptionOptions,
  RoomDataUpdate,
  RoomMessageEvent,
  ProposedMoveEvent,
  UpdateRoomDataOptions,
  RoomMessageRequest,
  ProposeMoveRequest,
  ProposeMoveResult,
  ValidateMoveVerdict,
  ValidateMoveResult,
} from './RoomsApi'
import { RundotGameRoom, RundotGameRoomPayload } from './RundotGameRoom'
import { getFirebaseClient, type FirebaseClient } from '../firebase/firebaseClient'

// Firestore types for snapshot callbacks
type DocumentSnapshot = import('firebase/firestore').DocumentSnapshot
type QuerySnapshot = import('firebase/firestore').QuerySnapshot
type DocumentChange = import('firebase/firestore').DocumentChange
type FirestoreError = import('firebase/firestore').FirestoreError

/**
 * Firestore-backed rooms API for data operations.
 */
export class FirestoreRoomsApi {
  private client: FirebaseClient | null = null
  private readonly activeSubscriptions: Set<() => void> = new Set()

  private async getClient(): Promise<FirebaseClient> {
    if (!this.client) {
      this.client = await getFirebaseClient()
    }
    return this.client
  }

  private getProfileId(): string {
    if (!this.client) {
      throw new Error('[RUN] Firebase not initialized. Call getClient() first.')
    }
    const profileId = this.client.getProfileId()
    if (!profileId) {
      throw new Error('[RUN] Not signed in. Click the RUN.world toolbar to sign in with Google.')
    }
    return profileId
  }

  // ===== ROOM DATA OPERATIONS =====

  async getRoomDataAsync(room: RundotGameRoom): Promise<Record<string, unknown>> {
    const client = await this.getClient()
    const roomRef = client.doc(client.firestore, `h5_rooms/${room.id}`)
    const snapshot = await client.getDoc(roomRef)

    if (!snapshot.exists()) {
      throw new Error(`Room ${room.id} not found`)
    }

    const roomData = snapshot.data()
    return (roomData?.data as Record<string, unknown>) || {}
  }

  async updateRoomDataAsync(
    room: RundotGameRoom,
    updates: Record<string, unknown>,
    options?: UpdateRoomDataOptions,
  ): Promise<void> {
    const client = await this.getClient()
    const roomRef = client.doc(client.firestore, `h5_rooms/${room.id}`)

    const merge = options?.merge !== false

    // Firestore semantics:
    // - Setting `data: updates` replaces the entire `data` map.
    // - Updating `data.<key>` fields merges individual keys into the `data` map.
    //
    // We support both:
    // - merge=true (default): merge updates into existing `data`
    // - merge=false: replace `data` entirely with `updates`
    if (merge) {
      const updateData: Record<string, unknown> = {
        updatedAt: client.serverTimestamp(),
      }

      for (const [key, value] of Object.entries(updates)) {
        // NOTE: Firestore interprets '.' as a path separator.
        // Callers should avoid keys with '.' unless they intend nested updates.
        updateData[`data.${key}`] = value
      }

      await client.updateDoc(roomRef, updateData)
      return
    }

    await client.updateDoc(roomRef, {
      data: updates,
      updatedAt: client.serverTimestamp(),
    })
  }

  // ===== MESSAGE OPERATIONS =====

  async sendRoomMessageAsync(room: RundotGameRoom, request: RoomMessageRequest): Promise<string> {
    const client = await this.getClient()
    const messagesRef = client.collection(client.firestore, `h5_rooms/${room.id}/messages`)

    const messageDoc = await client.addDoc(messagesRef, {
      roomId: room.id,
      senderId: this.getProfileId(),
      content: request.message.content || '',
      type: request.message.type || 'chat',
      metadata: request.metadata || {},
      timestamp: client.serverTimestamp(),
    })

    return messageDoc.id
  }

  // ===== PROPOSED MOVES =====

  async proposeMoveAsync(room: RundotGameRoom, request: ProposeMoveRequest): Promise<ProposeMoveResult> {
    const client = await this.getClient()
    const movesRef = client.collection(client.firestore, `h5_rooms/${room.id}/proposed_moves`)

    const moveDoc = await client.addDoc(movesRef, {
      proposerProfileId: this.getProfileId(),
      roomId: room.id,
      gameSpecificState: request.gameSpecificState,
      moveType: request.moveType,
      clientContext: request.clientContext || {},
      clientProposalId: request.clientProposalId,
      timestamp: client.serverTimestamp(),
      serverGenericValidationStatus: 'pending',
    })

    return { proposedMoveId: moveDoc.id }
  }

  async validateMoveAsync(
    room: RundotGameRoom,
    moveId: string,
    verdict: ValidateMoveVerdict,
  ): Promise<ValidateMoveResult> {
    const client = await this.getClient()
    const moveRef = client.doc(client.firestore, `h5_rooms/${room.id}/proposed_moves/${moveId}`)

    await client.updateDoc(moveRef, {
      serverGenericValidationStatus: verdict.isValid ? 'valid' : 'invalid',
      serverGenericValidationReason: verdict.reason || null,
      validatorId: verdict.validatorId || this.getProfileId(),
      updatedAt: client.serverTimestamp(),
    })

    return {
      success: true,
      moveId,
      isValid: verdict.isValid,
      reason: verdict.reason,
    }
  }

  // ===== SUBSCRIPTIONS =====

  async subscribeAsync(room: RundotGameRoom, options?: RoomSubscriptionOptions): Promise<() => void> {
    const client = await this.getClient()
    const unsubscribers: Array<() => void> = []

    const cleanupPartialSubscriptions = (): void => {
      for (const unsub of unsubscribers) {
        try {
          unsub()
        } catch (e) {
          console.error('[RUN] Error during room subscription cleanup:', e)
        }
      }
    }

    try {
      // Subscribe to room data
      if (options?.onData) {
        const roomRef = client.doc(client.firestore, `h5_rooms/${room.id}`)
        const unsubscribeData = client.onSnapshot(
          roomRef,
          (snapshot: DocumentSnapshot) => {
            if (snapshot.exists()) {
              const roomData = {
                id: snapshot.id,
                ...snapshot.data(),
              } as RundotGameRoomPayload

              const update: RoomDataUpdate = {
                type: 'H5_ROOM_DATA_UPDATED',
                roomId: room.id,
                roomData,
                timestamp: Date.now(),
              }
              options.onData!(update)
            }
          },
          (error: FirestoreError) => {
            console.error('[RUN] Room subscription error:', error)
          },
        )
        unsubscribers.push(unsubscribeData)
      }

      // Subscribe to messages
      if (options?.onMessages) {
        const messagesRef = client.collection(client.firestore, `h5_rooms/${room.id}/messages`)
        const messagesQuery = client.query(
          messagesRef,
          client.orderBy('timestamp', 'desc'),
          client.limit(50),
        )

        const unsubscribeMessages = client.onSnapshot(
          messagesQuery,
          (snapshot: QuerySnapshot) => {
            snapshot.docChanges().forEach((change: DocumentChange) => {
              const messageData = {
                id: change.doc.id,
                ...change.doc.data(),
              }

              let eventType: 'H5_ROOM_MESSAGE_RECEIVED' | 'H5_ROOM_MESSAGE_UPDATED' | 'H5_ROOM_MESSAGE_DELETED'
              if (change.type === 'added') {
                eventType = 'H5_ROOM_MESSAGE_RECEIVED'
              } else if (change.type === 'modified') {
                eventType = 'H5_ROOM_MESSAGE_UPDATED'
              } else {
                eventType = 'H5_ROOM_MESSAGE_DELETED'
              }

              const event: RoomMessageEvent = {
                type: eventType,
                roomId: room.id,
                message: messageData,
                timestamp: Date.now(),
              }
              options.onMessages!(event)
            })
          },
          (error: FirestoreError) => {
            console.error('[RUN] Messages subscription error:', error)
          },
        )
        unsubscribers.push(unsubscribeMessages)
      }

      // Subscribe to proposed moves (game events)
      if (options?.onGameEvents) {
        const movesRef = client.collection(client.firestore, `h5_rooms/${room.id}/proposed_moves`)
        const movesQuery = client.query(
          movesRef,
          client.orderBy('timestamp', 'desc'),
          client.limit(10),
        )

        const unsubscribeMoves = client.onSnapshot(
          movesQuery,
          (snapshot: QuerySnapshot) => {
            snapshot.docChanges().forEach((change: DocumentChange) => {
              const moveData = {
                id: change.doc.id,
                ...change.doc.data(),
              }

              const event: ProposedMoveEvent = {
                type: 'app:h5:proposedMoveValidationUpdated',
                roomId: room.id,
                proposedMoveData: moveData,
                proposedMoveId: change.doc.id,
                changeType: change.type,
                timestamp: Date.now(),
              }
              options.onGameEvents!(event)
            })
          },
          (error: FirestoreError) => {
            console.error('[RUN] Proposed moves subscription error:', error)
          },
        )
        unsubscribers.push(unsubscribeMoves)
      }
    } catch (error) {
      cleanupPartialSubscriptions()
      throw error
    }

    // Create combined unsubscribe function
    const unsubscribe = (): void => {
      for (const unsub of unsubscribers) {
        try {
          unsub()
        } catch (e) {
          console.error('[RUN] Error during room subscription cleanup:', e)
        }
      }
      this.activeSubscriptions.delete(unsubscribe)
    }

    this.activeSubscriptions.add(unsubscribe)
    return unsubscribe
  }

  /**
   * Clean up all active subscriptions.
   */
  destroy(): void {
    for (const unsubscribe of this.activeSubscriptions) {
      unsubscribe()
    }
    this.activeSubscriptions.clear()
  }
}
